import { TextField } from '@mui/material'
import React from 'react'
import { useForm, Controller, FormProvider } from 'react-hook-form'

import { LoadingButton } from 'components/common/LoadingButton'
import {
  getGcpAuthenticationFormInitializersNew,
  SERVICE_ACCOUNT_ID_FIELD_PROPS,
  type GcpAuthenticationFormValuesNew
} from 'components/common/mapped-entities/gcp/authentication/gcp-auth-form-utils'
import { getGcpAuthenticationPayload } from 'components/common/mapped-entities/gcp/authentication/gcp-integration-utils'
import { GcpAuthenticationSection } from 'components/common/mapped-entities/gcp/authentication/GcpAuthenticationSection'
import { useStatusCheckExternalIntegrationsContext } from 'context/status-checks/StatusCheckExternalIntegrationsContext'
import { errorToast } from 'lib/toast'
import { useCreateStatusCheckAuthentication } from 'queries/external-integrations/use-create-status-check-authentication'
import { type GcpIntegrationSetupResponse } from 'types/api/gcp/integrations'
import { type TeamExternalIntegrationGcp } from 'types/api/status-checks/status-check'
import { handleDataFetchOnChange } from 'utils/handle-data-fetch-on-change'
import { messageFromResponseError } from 'utils/strings'
import { type OnSuccessExternalIntegration } from 'utils/team-external-integrations/team-external-integrations-util'

export function GcpNewAuthenticationForm({
  gcpExternalIntegrations,
  gcpIntegrationSetupData,
  onSuccess
}: {
  gcpExternalIntegrations: TeamExternalIntegrationGcp[]
  gcpIntegrationSetupData: GcpIntegrationSetupResponse
  onSuccess?: OnSuccessExternalIntegration
}) {
  const { reFetchExternalIntegrations } =
    useStatusCheckExternalIntegrationsContext()

  const {
    isLoading: isLoadingCreateAuthentication,
    mutateAsync: createAuthentication
  } = useCreateStatusCheckAuthentication()

  const authForm = useForm<GcpAuthenticationFormValuesNew>({
    ...getGcpAuthenticationFormInitializersNew(gcpExternalIntegrations),
    mode: 'all'
  })

  const { control, formState, getValues } = authForm
  const { isSubmitting, isValid } = formState

  const handleSubmitAuthentication = async () => {
    try {
      const { serviceAccountId } = getValues()
      const body = getGcpAuthenticationPayload(serviceAccountId)
      const response = await createAuthentication(body)
      onSuccess?.(response)
      await handleDataFetchOnChange(reFetchExternalIntegrations)
    } catch (error) {
      errorToast(messageFromResponseError(error))
    }
  }

  const isSubmitDisabled = !isValid

  const isSubmitLoading = isLoadingCreateAuthentication || isSubmitting

  return (
    <FormProvider {...authForm}>
      <GcpAuthenticationSection {...gcpIntegrationSetupData}>
        <Controller
          control={control}
          name="serviceAccountId"
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...SERVICE_ACCOUNT_ID_FIELD_PROPS}
              error={Boolean(error?.message)}
              helperText={error?.message}
              {...field}
            />
          )}
        />
      </GcpAuthenticationSection>
      <LoadingButton
        isDisabled={isSubmitDisabled}
        isLoading={isSubmitLoading}
        sx={{ alignSelf: 'start', marginTop: 1 }}
        variant="contained"
        onClick={handleSubmitAuthentication}
      >
        {isSubmitLoading ? 'Saving' : 'Save'}
      </LoadingButton>
    </FormProvider>
  )
}
